/* eslint-disable */
import React, { useRef, useState, useEffect } from 'react'
import AiMessages from '../chat-messages/ai-messages'
import ClientMessages from '../chat-messages/client-messages'

declare global {
    interface Window {
        SpeechRecognition: any
        webkitSpeechRecognition: any
    }
}

type SpeechRecognition = any
type SpeechRecognitionEvent = any

interface IChatInput {
    chatData: any;
    onGettingChatData: (newChatData?: any) => void;
}

const ChatInput = ({
    chatData,
    onGettingChatData
}: IChatInput) => {
    const [message, setMessage] = useState('')
    const [isTyping, setIsTyping] = useState(false);
    const [allMessages, setAllMessages] = useState<any>([])
    const [imagePreview, setImagePreview] = useState<string | null>(null)
    const [isListening, setIsListening] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const recognitionRef = useRef<SpeechRecognition | null>(null)
    const bottomRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        setAllMessages(chatData)
    }, [chatData])

    useEffect(() => {
        const SpeechRecognition =
            (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
        if (SpeechRecognition) {
            const recognition = new SpeechRecognition()
            recognition.continuous = false
            recognition.interimResults = false
            recognition.lang = 'en-US'

            recognition.onresult = (event: SpeechRecognitionEvent) => {
                const transcript = event.results[0][0].transcript
                setMessage((prev) => `${prev} ${transcript}`.trim())
            }

            recognition.onerror = (event: any) => {
                console.error('Speech recognition error:', event.error)
                setIsListening(false)
            }

            recognition.onend = () => {
                setIsListening(false)
            }

            recognitionRef.current = recognition
        } else {
            console.warn('Speech recognition is not supported in this browser.')
        }
    }, [])

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [allMessages, isTyping])

    const handleMicClick = () => {
        if (!recognitionRef.current) return

        if (isListening) {
            recognitionRef.current.stop()
            setIsListening(false)
        } else {
            recognitionRef.current.start()
            setIsListening(true)
        }
    }

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            const reader = new FileReader()
            reader.onloadend = () => setImagePreview(reader.result as string)
            reader.readAsDataURL(file)
        }
    }

    const handleSend = () => {
        const formatAllMessages = [
            ...allMessages || [],
            {
                'user': {
                    message: message,
                    sort: allMessages?.length + 2
                }
            }
        ]
        setAllMessages(formatAllMessages)
        onGettingChatData(formatAllMessages)
        setMessage('')
        setImagePreview(null)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSend()
        }
    }

    return (
        <div className='min-w-[90vw] max-w-[90vw] md:min-w-[400px] md:max-w-[400px] p-2'>
            <div className='max-h-[40vh] overflow-y-auto min-h-[300px]'>
                {allMessages && allMessages.length > 0 && allMessages.map((item: any, idx: number) => (
                    <div key={idx}>
                        {item?.agent?.message ? (
                            <div className='flex flex-row items-start'>
                                {(idx > 0
                                    ? (allMessages?.[idx - 1] && !allMessages?.[idx - 1]?.agent?.message)
                                    : !!allMessages?.[idx]?.agent?.message) ? (
                                    <div className='mt-4 mr-2'>
                                        <img src={'/assets/agents/charles-agent.svg'} className='object-contain min-w-[35px] !rounded-full' />
                                    </div>
                                ) : (
                                    <div className='min-w-[35px] mr-2' />
                                )}
                                <AiMessages
                                    text={item?.agent?.message}
                                    isTyping={isTyping}
                                    setIsTyping={setIsTyping}
                                    isNew={idx === allMessages.length - 1}
                                />
                            </div>
                        ) : (
                            <ClientMessages text={item?.user?.message} />
                        )}
                    </div>
                ))}
                <div ref={bottomRef} />
            </div>

            <div>
                <div className={`flex items-center rounded-full border  ${isTyping ? 'border-gray-300' : 'border-[#00330F]'} bg-gradient-to-r from-white to-blue-50 px-4 py-2 shadow-sm w-full`}>
                    <button
                        type='button'
                        disabled={isTyping}
                        onClick={handleMicClick}
                        aria-label={isListening ? 'Stop listening' : 'Start listening'}
                        className={`text-green-600 mr-2 relative ${isTyping ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <div className={`w-[30px] h-[30px] ${isListening ? 'animate-pulse' : ''}`}>
                            <img
                                src={'/assets/chat/microphone.svg'}
                                className='object-contain w-full h-full'
                                alt='Microphone'
                            />
                            {isListening && (
                                <span className="absolute top-0 left-0 w-full h-full rounded-full border-2 border-green-400 animate-ping z-[-1]" />
                            )}
                        </div>
                    </button>
                    <input
                        type='text'
                        placeholder='Write Something'
                        disabled={isTyping}
                        className='flex-1 outline-none bg-transparent text-gray-700 placeholder-gray-400'
                        value={message}
                        onKeyDown={handleKeyDown}
                        onChange={(e) => setMessage(e.target.value)}
                    />

                    <input
                        type='file'
                        accept='image/*'
                        className='hidden'
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                    />

                    <button
                        type='button'
                        onClick={() => fileInputRef.current?.click()}
                        className={`text-green-600 mx-2 ${isTyping ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <img src={'/assets/chat/file-attach.svg'} className='object-contain w-[30px]' />
                    </button>

                    <button
                        type='button'
                        disabled={isTyping}
                        onClick={handleSend}
                        className={`text-green-600 ml-2 ${isTyping ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <img src={'/assets/chat/send.svg'} className='object-contain w-[30px]' />
                    </button>
                </div>
                {imagePreview && (
                    <div className='mt-2 rounded-md overflow-hidden border border-gray-200 w-32'>
                        <img src={imagePreview} alt='preview' className='object-cover w-full h-24' />
                    </div>
                )}
            </div>
        </div>
    )
}

export default ChatInput
